// The X-ray manifest: one declaration per lens, and the CONTAINMENT boundary.
//
// This file exists because the surface area was the real problem. Building the
// first two X-rays by hand touched six files scattered across an application —
// an extractor, a substrate table, two render axes, a feed registry, a tab strip
// — with nothing naming that set. An agent asked to "add an X-ray" therefore had
// an unbounded surface, and every session sprawled across it.
//
// A manifest fixes the surface. The agent authors ONE descriptor plus its own
// directory; every other touchpoint is DECLARED here rather than edited in place,
// and the host application reads the declaration. That is the difference between
// an agent editing an app and an agent filling in a contract.
//
// The fields are not a wish list. Each one is a wiring point that was discovered
// by getting it wrong: a lens with no substrate scans the wrong window, a lens
// with no feed domain shows up as unclassified residual in the per-trace reading,
// a lens with no empty note renders a blank panel that reads as broken.

const ID_RE = /^[a-z][a-z0-9-]{1,23}$/;

/**
 * Declare an X-ray. Returns the descriptor, marked with any contract violations.
 *
 * IT MUST NOT THROW. This runs at static-import time, and the import chain reaches
 * the core trace-detail view unconditionally — xrays/index.js is pulled in by both
 * traceSemanticFeed and XRayBody. A throw here does not produce a bad lens, it
 * produces a blank trace view for every user, which is the opposite of the property
 * the registry was built to guarantee: one bad manifest must not blank the catalog.
 *
 * So validation is recorded rather than raised. `createRegistry` already filters
 * invalid manifests and reports their problems, and it is the only consumer that
 * needs to act on them.
 */
export function defineXray(desc) {
  const { ok, problems } = validateXray(desc);
  if (!ok) {
    // Visible without being fatal. A manifest author sees this immediately in dev;
    // a user never sees a blank panel because of it.
    // eslint-disable-next-line no-console
    console.warn(`[xray] invalid manifest${desc?.id ? ` (${desc.id})` : ""}:\n  ${problems.join("\n  ")}`);
  }
  return ok ? desc : { ...desc, __invalid: problems };
}

/**
 * Check a descriptor against the wiring contract.
 *
 * Rejects rather than repairs. A manifest with a missing field produces a lens
 * that is silently wrong somewhere specific — a wrong scan window, an invisible
 * domain, a blank panel — and those are expensive to notice later.
 */
export function validateXray(desc) {
  const problems = [];
  const d = desc && typeof desc === "object" ? desc : {};

  if (!ID_RE.test(d.id || "")) {
    problems.push("id: required, lowercase kebab, 2-24 chars — it keys the tab, the feed domain and the audit report");
  }
  if (!d.label || String(d.label).length > 12) {
    problems.push("label: required, <=12 chars — it sits in a tab strip that already overflows");
  }
  // The tooltip is the only place a reader learns what a lens covers before
  // opening it. A lens nobody can identify from the strip does not get opened.
  if (!d.title || String(d.title).length < 30) {
    problems.push("title: required, >=30 chars — say what work this covers and what a reader gets, not just the label again");
  }

  // WHO READS THIS. Carried from the judge's verdict, and required here so it
  // cannot be dropped between deciding to build and building. A lens with no
  // named consumer is a data dump.
  if (!d.consumer) problems.push("consumer: required — who opens this lens");
  if (!d.decision) problems.push("decision: required — what that person decides differently once they can see it");

  // The scan window. Without one the lens samples the newest N prompts and shows
  // an empty panel whenever its substrate is sparse, which reads as a bug.
  //
  // Only tab-bearing lenses need one. A trace-only lens has no window to
  // down-select, so demanding a substrate would be asking for a filter nothing
  // reads.
  if (d.tab !== false) {
    if (!d.substrate || typeof d.substrate.match !== "function") {
      problems.push("substrate.match(toolName): required — down-selects the scan window to substrate-bearing prompts");
    } else if (!d.substrate.label) {
      problems.push("substrate.label: required — shown in the status line so a reader knows what was scanned");
    }
  }

  // THE AXIS DECISION, and the one this contract originally got wrong by assuming.
  //
  // Every lens used to get a scope tab, because the first several all deserved one.
  // But the two axes ask different questions. A scope tab reads MANY traces through
  // one lens, so it only earns its place when the ops have a target in the world —
  // a file, a dashboard, a query, a published artifact — that aggregates into a
  // pattern across sessions. A lens whose subject is the agent's own execution
  // (control flow, retries, waiting) has no such target: grouped across sessions it
  // collapses into mechanism buckets no reader acts on, while the same op read
  // inline next to what the agent did next is genuinely informative.
  //
  // So `tab: false` is a first-class choice, not a degraded one — and a lens on
  // NEITHER axis is the actual mistake.
  if (d.tab === false && d.feed === false) {
    problems.push("tab: false with feed: false — a lens on neither axis renders nowhere");
  }
  if (d.tab === false && !d.tabReason) {
    problems.push("tab: false: give tabReason — why this reads as process-level rather than as a cross-session pattern");
  }

  // The per-trace axis. Omitting it does not mean "no feed row"; it means the
  // work shows up as unclassified residual, which is worse than absent.
  if (d.feed !== false) {
    if (!d.feed || !d.feed.domain) {
      problems.push("feed.domain: required (or feed: false with a reason) — otherwise this work reads as unclassified residual in the per-trace feed");
    } else if (d.feed.domain !== d.id && !d.feed.reason) {
      problems.push("feed.domain differs from id: give feed.reason");
    }
  }

  if (typeof d.extract !== "function") problems.push("extract(turns): required");
  if (typeof d.collect !== "function") problems.push("collect(promptEvents): required — use src/build's collectWindowOps");
  if (typeof d.group !== "function") problems.push("group(flatOps): required — use src/build's groupActivity");

  // A panel with nothing to show must SAY so. A blank region is
  // indistinguishable from a broken one, and both of the first two lenses were
  // reported as broken while behaving correctly.
  if (!d.emptyNote) {
    problems.push("emptyNote: required — what the panel says when the window holds none of this work");
  }
  // And an op with nothing to show must say why. Arrived at independently in two
  // extractors, which is why it is in the contract rather than in advice.
  if (typeof d.explainMissing !== "function") {
    problems.push("explainMissing(op): required — why THIS op has no payload (returns null when it has one)");
  }

  // THE PROVENANCE AXIS. A third place a lens can render, after the tab strip and
  // the per-trace feed: the lineage graph, which asks what fed each thing a window
  // produced. A lens joins it by describing its own projection into a node.
  //
  // Functions of an op, not a field-name map, because the identity is usually
  // DERIVED — a service in its environment, a package in its registry, a remote
  // prefix — and because only the lens knows which of its own kinds changed
  // something. A mapping table cannot express either, and the host guessing on the
  // lens's behalf is exactly what this field replaces.
  //
  // Optional, but the decision is not: a lens that contributes nothing says why
  // here, next to itself, the same way `tab: false` owes a `tabReason`. Otherwise
  // the reasoning ends up in a list somewhere else that nobody updates.
  if (d.lineage !== undefined) {
    const lin = d.lineage && typeof d.lineage === "object" ? d.lineage : {};
    if (!lin.kind || typeof lin.kind !== "string") {
      problems.push("lineage.kind: required — the node kind this lens contributes to the graph");
    }
    for (const fn of ["ref", "role", "text", "label"]) {
      if (typeof lin[fn] !== "function") {
        problems.push(`lineage.${fn}(op): required — a function of one op, since the value is derived rather than a field name`);
      }
    }
    if (lin.intent !== undefined && typeof lin.intent !== "function") {
      problems.push("lineage.intent: optional, but must be a function of an op when present");
    }
    // Whether this lens's ref is a COORDINATE — something another artifact could
    // name — rather than a bare word. Only a coordinate can carry a citation, the
    // graph's strongest claim; a unit name like `forgejo` or a package name would
    // manufacture derivation out of a coincidence.
    //
    // The lens declares it because the lens resolves the ref and knows its shape.
    // It replaced a set of kinds kept in the host, which meant a new lens could
    // become citable only by someone remembering to edit a file it has never
    // heard of. Absent means not citable — the safe default, and no reason is
    // demanded here because the weaker tiers still carry the op.
    if (lin.citable !== undefined && typeof lin.citable !== "boolean") {
      problems.push("lineage.citable: optional, but must be a boolean — the lens stating whether its ref is a coordinate another artifact could name");
    }
    // A SUBSTRATE IS REQUIRED ONCE A LENS PROJECTS INTO THE GRAPH, even with no
    // tab of its own. The lineage window is down-selected like any other, so a
    // lens that cannot say which tool names carry its work is one the window can
    // never sample — it declares a projection and contributes nothing, which reads
    // as "this lens found nothing" rather than "this lens was never looked for".
    // Seven trace-only lenses were in exactly that state: substrate was demanded
    // only of tab-bearing lenses, because when that rule was written a substrate's
    // only job was down-selecting its OWN tab.
    if (!d.substrate || typeof d.substrate.match !== "function") {
      problems.push("lineage declared without substrate.match(toolName): the lineage window is down-selected too, so a lens with no matcher can never be sampled into it");
    }
  } else if (!d.lineageReason) {
    problems.push("lineage omitted: give lineageReason — why this lens's ops are not provenance nodes, recorded beside the lens rather than in a list elsewhere");
  }

  if (d.gate !== undefined && typeof d.gate !== "string") {
    problems.push("gate: optional, but must be a flag name when present");
  }
  return { ok: problems.length === 0, problems };
}

// ── containment ──────────────────────────────────────────────────────────────

/**
 * The files an X-ray agent may create or modify, as glob-ish prefixes relative to
 * the repository root, given the lens id.
 *
 * This is the fixed surface area. Everything else — the tab strip, the feed
 * registry, the substrate table, shared primitives — is READ-ONLY to the agent
 * and driven from the manifest instead. The point is not permissions
 * enforcement; it is that a bounded surface is what lets an agent iterate and
 * apply judgment without the work sprawling.
 */
export function surfaceFor(id) {
  if (!ID_RE.test(id || "")) throw new Error(`surfaceFor: invalid id ${id}`);
  return {
    writable: [
      `extractors/${id}/`,      // grammar, spec, tests — the agent's own room
      `xrays/${id}.xray.js`,    // the one declaration the host reads
      // The panel lives HERE, not in the host's shared panel component. That file
      // is thousands of lines serving every lens, so an agent writing into it has
      // an unbounded surface by construction — which is the thing containment
      // exists to prevent. One file per lens is what makes the render stage
      // containable at all.
      `xrays/${id}/`,
    ],
    // Appending a row is allowed; editing existing rows is not. A new defect
    // becomes a new row, and rewriting someone else's row erases a regression.
    appendOnly: ["test/audit.test.js"],
    readOnly: [
      "src/",                   // shared primitives — a fix here is a shared fix
      "xrays/index.js",         // generated from the manifests
      "AGENT.md", "README.md",
    ],
  };
}

/**
 * Is a path inside the declared surface for this lens?
 *
 * Used by the loop's build gate. An agent that had to touch something outside
 * its surface has found either a missing primitive or a missing manifest field,
 * and both are worth surfacing rather than absorbing.
 */
export function withinSurface(id, path) {
  const s = surfaceFor(id);
  const p = String(path || "").replace(/^\.\//, "");
  if (s.writable.some((w) => p.startsWith(w))) return { ok: true, kind: "writable" };
  if (s.appendOnly.some((a) => p === a)) return { ok: true, kind: "append-only" };
  return {
    ok: false,
    kind: "outside",
    why: `${p} is outside the surface for "${id}". Touching it means a primitive or a `
      + "manifest field is missing — say which, rather than editing around it.",
  };
}
